import { useTables } from './TablesContext';
import { useDbFunctions } from '@/talons';
import { getOnStorage, removeFromStorage, setOnStorage } from '@/shared';
import { useState, useEffect, useContext, useCallback, createContext, useMemo } from 'react';
import { ClientProps, ClientAddressProps, CampanhaProdutosProps, CampanhaClientesProps } from '@/types';

interface HookProps {
  janelaEntregaData: any;
  allClients: ClientProps[];
  selectedClient: ClientProps;
  campanhaProdutos: CampanhaProdutosProps;
  campanhaClientes: CampanhaClientesProps;
  clientPayments: ClientProps['COD_PAGAMENTO'];
  campanhaTopMix: any;
  campanhaFaixa: any;
  selectedClientAddress: ClientAddressProps;
  indicadorLoja: any;
  indicadoresProdutividade: any;

  logoutClient: () => void;
  calculateDeliveryDate: () => string;
  setJanelaEntregaData: (data: any) => void;
  setAllClients: (clients: ClientProps[]) => void;
  setSelectedClient: (client: ClientProps) => void;
  setCampanhaClientes: (campanha: CampanhaClientesProps) => void;
  setCampanhaProdutos: (campanha: CampanhaProdutosProps) => void;
  setPositivacao: (mode: 'produto' | 'cliente', ids: number[]) => void;
  setCampanhaTopMix: (campanha: any) => void;
  setCampanhaFaixa: (campanha: any) => void;
  setIndicadorLoja: (indicador: any) => void;
  setIndicadoresProdutividade: (indicadores: any) => void;
}

const ConfigsContex = createContext<HookProps>({} as HookProps);

const reduceLsit = (list?: number[]) => list?.reduce((acc, p) => ({ [p]: p, ...acc }), {}) || {};

const ClientProvider: React.FC<any> = ({ children }) => {
  const { tables } = useTables();
  const {getOnDB} = useDbFunctions();

  const client = getOnStorage('selected_client');

  const [allClientsB, setAllClients] = useState<any>();
  const [janelaEntrega, setJanelaEntrega] = useState<any>(null);
  const [campanhaFaixa, setHookCampanhaFaixa] = useState<any>(null);
  const [indicadorLoja, setHookIndicadorLoja] = useState<any>(null);
  const [campanhaTopMix, setHookCampanhaTopMix] = useState<any>(null);
  const [janelaEntregaData, setJanelaEntregaData] = useState<any>(null);
  const [hookSelectedClient, setHookSelectedClient] = useState(client || {} as ClientProps);
  const [indicadoresProdutividade, setHookIndicadoresProdutividade] = useState<any>(null);
  const [campanhaClientes, setHookCampanhaClientes] = useState({} as CampanhaClientesProps);
  const [campanhaProdutos, setHookCampanhaProdutos] = useState({} as CampanhaProdutosProps);

  useEffect(() => {
    const loadJanelaEntrega = async () => {
      const janelaEntregaData = await getOnDB('janela_entrega');
      setJanelaEntrega(janelaEntregaData);
    };

    if(!tables.current.janela_entrega) {
      loadJanelaEntrega();
    }
    
  }, [getOnDB, tables]);
  
  const selectedClientAddress = useMemo(() => {
    const clientHook = {...(hookSelectedClient || {})};
    return {
      street: clientHook.ENDERECO,
      address_number: clientHook.NUMERO,
      region: clientHook.BAIRRO,
      state: clientHook.ESTADO,
      city: clientHook.CIDADE,
      name: clientHook.NM_CLIENTE,
      cep: clientHook.CEP,
      telephone: clientHook.NR_TELEFONE,
    };
  }, [hookSelectedClient]);

  const selectedClient = useMemo(() => {
    const clientHook = {...(hookSelectedClient || {})};
    clientHook.COD_PAGAMENTO = clientHook?.COD_PAGAMENTO?.sort((a, b) => a.cod_condicao - b.cod_condicao) || [];
    return clientHook
  }, [hookSelectedClient]);

  const clientPayments = useMemo(() => {
    return selectedClient?.COD_PAGAMENTO?.sort((a, b) => a.cod_condicao - b.cod_condicao) || [];
  }, [selectedClient]);

  const findRouteByCode = useCallback((codRota: number) => {
    if(!janelaEntrega || !janelaEntrega.length || !Array.isArray(janelaEntrega) || !codRota) return null;

    for (const rota of janelaEntrega) {
      if (rota.JSON_RESULTADO && Array.isArray(rota.JSON_RESULTADO.rota)) {
        const rotaEncontrada = rota.JSON_RESULTADO.rota.find((rota: any) => rota.codRota === codRota);
        
        if (rotaEncontrada) {
          return {
            codLoja: rota.COD_LOJA,
            rota: rotaEncontrada
          };
        }
      }
    }
  }, [janelaEntrega]);

  const calculateDeliveryDate = useCallback(() => {
    const cod_rota = client?.COD_ROTA;
    
    if (!cod_rota) {
      return 'Nenhuma janela de entrega disponível no momento.';
    }
    
    const days = {
      1: 'Domingo',
      2: 'Segunda-feira',
      3: 'Terça-feira',
      4: 'Quarta-feira',
      5: 'Quinta-feira',
      6: 'Sexta-feira',
      7: 'Sábado',
    };

    const result = findRouteByCode(cod_rota);

    if (!result || !result.rota || !result.rota.entregas || result.rota.entregas.length === 0) {
      return 'Nenhuma janela de entrega disponível para esta rota.';
    }

    const now = new Date();
    const weekDayNow = now.getDay();
    const hour = now.getHours();
    const weekDay = weekDayNow === 0 ? 7 : weekDayNow;

    for (const entrega of result.rota.entregas) {
      const initialDay = entrega.diaInicial;
      const initialHour = entrega.horaInicial;
      const finalDay = entrega.diaFinal;
      const finalHour = entrega.horaFinal;

      const isWithinRange =
        (weekDay > initialDay || (weekDay === initialDay && hour >= initialHour)) &&
        (weekDay < finalDay || (weekDay === finalDay && hour < finalHour));

      if (isWithinRange) {
        return `Data prevista de entrega: ${entrega.dataPrevistaEntrega} (${days[initialDay as keyof typeof days]})`;
      }
    }
    
    return 'Nenhuma janela de entrega disponível no momento.';
  }, [client, findRouteByCode]);

  useEffect(() => {
    if (tables?.current?.cliente) {
      setAllClients(tables.current.cliente);
    }
  }, []);

  const setSelectedClient = useCallback(
    (newClient: ClientProps) => {
      setOnStorage('selected_client', newClient);
      setHookSelectedClient(newClient);
    },
    [setHookSelectedClient]
  );

  const logoutClient = useCallback(() => {
    removeFromStorage('selected_client');
    setHookSelectedClient({} as ClientProps);
    localStorage.setItem('@PWA_RCA:selected_client', '');
  }, [setHookSelectedClient]);

  const setCampanhaProdutos = useCallback((campanha: CampanhaProdutosProps) => {
    if (!campanha) return;

    if (campanha?.ativo?.produto) campanha.ativo.ids = reduceLsit(campanha?.ativo?.produto);
    if (campanha?.inativo?.produto) campanha.inativo.ids = reduceLsit(campanha?.inativo?.produto);

    setHookCampanhaProdutos(campanha);
  }, []);

  const setCampanhaClientes = useCallback((campanha: CampanhaClientesProps) => {
    if (!campanha) return;

    if (campanha?.ativo?.clientes) campanha.ativo.ids = reduceLsit(campanha?.ativo?.clientes);
    if (campanha?.inativo?.cliente) campanha.inativo.ids = reduceLsit(campanha?.inativo?.cliente);

    setHookCampanhaClientes(campanha);
  }, []);

  const setCampanhaTopMix = useCallback((campanha: any) => {
    if (!campanha) return;

    setHookCampanhaTopMix(campanha);
  }, []);

  const setCampanhaFaixa = useCallback((campanha: any) => {
    if (!campanha) return;

    setHookCampanhaFaixa(campanha);
  }, []);

  const setIndicadoresProdutividade = useCallback((indicadores: any) => {
    if (!indicadores) return;

    setHookIndicadoresProdutividade(indicadores);
  }, []);

  const setIndicadorLoja = useCallback((indicador: any) => {
    if (!indicador) return;

    setHookIndicadorLoja(indicador);
  }, []);

  const setPositivacao = useCallback((mode: 'produto' | 'cliente', ids: number[]) => {
    // const isProduct = mode === 'produto' 
    // if (isProduct) {
    //   const campanha = {...campanhaProdutos};
    //   campanha.inativo.produto = campanha.inativo.produto.filter((p: number) => !ids.includes(p))
    //   campanha.ativo.produto = [...campanha.ativo.produto, ...ids]

    //   setCampanhaProdutos(campanha)
    //   setOnDB(campanha, 'campanhaProduto')
    //   return
    // }

    // const campanha = {...campanhaClientes};
    // campanha.inativo.cliente = campanha.inativo.cliente.filter((p: number) => !ids.includes(p))
    // campanha.ativo.clientes = [...campanha.ativo.clientes, ...ids]

    // setCampanhaClientes(campanha)
    // setOnDB(campanha, 'campanhaCliente')
    // }, [campanhaProdutos, campanhaClientes, setCampanhaProdutos, setCampanhaClientes, setOnDB]);
  }, []);

  useEffect(() => {
    const getCampanhas = async () => {
      const campanhaProdutos = tables.current.campanha_produto;
      setCampanhaProdutos(campanhaProdutos);
      const campanhaClientes = tables.current.campanha_cliente;
      setCampanhaClientes(campanhaClientes);
    };

    getCampanhas();
  }, []);

  useEffect(() => {
    if (!indicadorLoja) {
      setIndicadorLoja(tables.current.indicador_loja);
    }
  }, [indicadorLoja]);

  const allClients = useMemo(() => (Array.isArray(tables?.current?.cliente) ? tables.current.cliente : []), [tables, allClientsB]);
  

  return (
    <ConfigsContex.Provider
      value={{
        clientPayments,
        campanhaProdutos,
        campanhaClientes,
        allClients,
        selectedClient,
        selectedClientAddress,
        janelaEntregaData,
        campanhaFaixa,
        campanhaTopMix,
        indicadorLoja,
        indicadoresProdutividade,
        // loadClients,
        logoutClient,
        setAllClients,
        setPositivacao,
        setSelectedClient,
        setCampanhaProdutos,
        setCampanhaClientes,
        setJanelaEntregaData,
        calculateDeliveryDate,
        setCampanhaFaixa,
        setCampanhaTopMix,
        setIndicadorLoja,
        setIndicadoresProdutividade,
      }}
    >
      {children}
    </ConfigsContex.Provider>
  );
};

const useClient = (): HookProps => {
  const ctx = useContext(ConfigsContex);

  if (!ctx) throw new Error('Erro ao usar hook Header');

  return ctx;
};

export { ClientProvider, useClient };
